import { networkApi } from '../infrastructure/api/NetworkApiClient';
import { LoadProfileType } from '../types/network';
import { CONTROL_FALLBACK_START_FACTOR_DEFAULT, CONTROL_MANUAL_AFTER_NOT_AUS_MINUTES_DEFAULT, CONTROL_GROUP_START_OFFSET_SECONDS_DEFAULT, CONTROL_RELEASE_STEPS_DEFAULT, CONTROL_SAFETY_MARGIN_PCT_DEFAULT } from '../utils/controlConstants';
import {
  LOADING_COMPLIANCE_LIMIT_PCT,
  LOADING_DEFAULT_GREEN_PCT,
  LOADING_DEFAULT_YELLOW_PCT,
  LOADING_WARNING_PCT,
} from '../utils/loadingConstants';

export interface LoadLevelThresholds {
  green: number;
  yellow: number;
  orange: number;
  red: number;
}

export interface VoltageThresholds {
  /** Controls which Hausanschlüsse are included in voltage monitoring.
   * Voltage limits are defined by DIN EN 50160 — see voltageConstants.ts. */
  voltageMonitoringSource: 'smgw_only' | 'smgw_with_powerflow';
}

/**
 * Operational (instance-configurable) loading Ampel. Fuses follow the cable levels —
 * a fuse protects the cable it sits on. The legal limits (100 % I_n, EN 50160 band)
 * are constants (loadingConstants.ts / voltageConstants.ts), never settings.
 */
export interface ThresholdSettings {
  cableLoadLevels: LoadLevelThresholds;
  transformerLoadLevels: LoadLevelThresholds;
  voltageThresholds: VoltageThresholds;
}

export interface TransformerSettings {
  defaultRatedPowerKva: number;
  defaultVk: number;
  defaultVkr: number;
  defaultPfe: number;
  defaultI0: number;
  defaultVector: string;
  defaultTapNeutral: number;
  defaultTapMin: number;
  defaultTapMax: number;
  defaultTapStep: number;
}

export interface CableTypeProperties {
  rPerKm: number;
  xPerKm: number;
  maxCurrentA: number;
  cPerKm: number;
}

export interface DefaultCableSettings {
  defaultUnknownCableType: string;
  defaultCableTypes: Record<string, CableTypeProperties>;
}

export interface ConsumptionRange {
  min: number;
  max: number;
}

export interface TimeValue {
  time: string;
  value: number;
}

export interface SeasonalValues {
  dayTypes: Record<string, TimeValue[]>;
}

export interface LoadProfileValues {
  seasons: Record<string, SeasonalValues>;
}

export interface LoadProfileSettings {
  defaultResidentialProfile: LoadProfileType;
  defaultCommercialProfile: LoadProfileType;
  defaultIndustrialProfile: LoadProfileType;
  defaultPowerFactor: number;
  consumptionRanges: Record<LoadProfileType, ConsumptionRange>;
  profileValues: Record<LoadProfileType, LoadProfileValues>;
}

export interface MapSettings {
  houseLoadColoring: boolean;
  ringCircuitHighlighting: boolean;
}

/**
 * Netzanschlussprüfung: how the base state of the OTHER connections is
 * constructed. Operational planning choice, not a legal value — the fixed
 * limits, the applicant's asset at P_Amax and the reservation rule apply in
 * both modes (doc/FNN_VDE/netzanschlusspruefung/README.md §3c).
 */
export type LoadCaseMode = 'synthetic' | 'archive';

export interface NetzanschlussSettings {
  loadCaseMode: LoadCaseMode;
}

/**
 * §14a control (Schaltengine) — operator settings, stored as `controlSettings` (backend ControlSettings,
 * doc/Architektur/STEUERUNG.md 7.2). The regulatory figures (80 %, Un − 8 %, 5 min, 15-min slots) are constants
 * in utils/controlConstants.ts and not editable. Only the proposal mode exists: every command needs an approval.
 */
export type ControlMode = 'proposal' | 'auto' | 'auto_after_timeout' | 'auto_outside_hours';

export interface ControlSettings {
  enabled: boolean;
  mode: ControlMode;
  triggerOnTrafoOverload: boolean;
  triggerOnCableOverload: boolean;
  triggerOnUndervoltage: boolean;
  safetyMarginPercent: number;
  groupStartOffsetSeconds: number;
  maxSlotsAhead: number;
  holdSlotsAfterClear: number;
  /** Minutes after a Not-Aus in which proposals are never executed automatically (7.2 Nr. 14). */
  manualAfterNotAusMinutes: number;
  /** Start factor when the requirement cannot be sized (undervoltage without load flow, overload without measured flow). */
  fallbackStartFactor: number;
  /** Steps of the return to normal: the reduction falls by at most 1/releaseSteps of its peak per slot (7.2 Nr. 12). */
  releaseSteps: number;
  autoRelease: boolean;
  requireMakoKeys: boolean;
  /** auto_after_timeout: seconds after the proposal. */
  autoAfterSeconds: number;
  /** auto_outside_hours: staffed hours (Berlin, "HH:mm") and ISO weekdays (1 = Monday). */
  staffedFrom: string;
  staffedUntil: string;
  staffedWeekdays: number[];
  /** Roles allowed to decide; empty = every logged-in user. */
  approverRoles: string[];
}

export const DEFAULT_CONTROL_SETTINGS: ControlSettings = {
  enabled: false,
  mode: 'proposal',
  triggerOnTrafoOverload: true,
  triggerOnCableOverload: true,
  triggerOnUndervoltage: true,
  safetyMarginPercent: CONTROL_SAFETY_MARGIN_PCT_DEFAULT,
  groupStartOffsetSeconds: CONTROL_GROUP_START_OFFSET_SECONDS_DEFAULT,
  maxSlotsAhead: 1,
  holdSlotsAfterClear: 1,
  releaseSteps: CONTROL_RELEASE_STEPS_DEFAULT,
  fallbackStartFactor: CONTROL_FALLBACK_START_FACTOR_DEFAULT,
  manualAfterNotAusMinutes: CONTROL_MANUAL_AFTER_NOT_AUS_MINUTES_DEFAULT,
  autoRelease: true,
  requireMakoKeys: false,
  autoAfterSeconds: 120,
  staffedFrom: '07:00',
  staffedUntil: '16:00',
  staffedWeekdays: [1, 2, 3, 4, 5],
  approverRoles: [],
};

export interface Settings {
  transformerSettings: TransformerSettings;
  loadProfileSettings: LoadProfileSettings;
  thresholdSettings: ThresholdSettings;
  defaultCableSettings: DefaultCableSettings;
  mapSettings: MapSettings;
  /** Optional on older backends / stored settings — treat missing as synthetic. */
  netzanschlussSettings?: NetzanschlussSettings;
  /** §14a control; optional on older backends — treat missing as DEFAULT_CONTROL_SETTINGS. */
  controlSettings?: ControlSettings;
  /**
   * Base URL of the MaaS frontend for SMGW deep links. No longer editable in the
   * settings UI (removed 2026-09-17); a value stored on an instance stays effective.
   */
  maasUrl: string;
}

const DEFAULT_LOAD_LEVELS: LoadLevelThresholds = {
  green: LOADING_DEFAULT_GREEN_PCT,
  yellow: LOADING_DEFAULT_YELLOW_PCT,
  orange: LOADING_WARNING_PCT,
  red: LOADING_COMPLIANCE_LIMIT_PCT,
};

/**
 * Frontend threshold defaults, used until the instance settings are loaded.
 * Matches backend defaults in Settings.cs (which derive from LoadingConstants.cs).
 */
export const DEFAULT_THRESHOLD_SETTINGS: ThresholdSettings = {
  cableLoadLevels: { ...DEFAULT_LOAD_LEVELS },
  transformerLoadLevels: { ...DEFAULT_LOAD_LEVELS },
  voltageThresholds: { voltageMonitoringSource: 'smgw_with_powerflow' },
};

class SettingsService {
  async getSettings(): Promise<Settings> {
    return networkApi.getSettings<Settings>();
  }

  async updateSettings(settings: Settings): Promise<void> {
    return networkApi.updateSettings(settings);
  }

  async updateSection<T>(section: string, data: T): Promise<void> {
    return networkApi.patchSettings(section, data);
  }

  async getLoadProfileValues(profileType: LoadProfileType): Promise<LoadProfileValues> {
    return networkApi.getLoadProfileValues<LoadProfileValues>(profileType);
  }

  getLoadProfileDescription(profileType: LoadProfileType): string {
    const descriptions: Record<LoadProfileType, string> = {
      'H0': 'Haushalt',
      'G0': 'Allgemeine Gewerbe',
      'G1': 'Wochenendintensives Gewerbe',
      'G2': 'Abendintensives Gewerbe',
      'G3': 'Tagesintensives Gewerbe',
      'G4': 'Saisonales Gewerbe',
      'G5': 'Dauerintensives Gewerbe',
      'G6': 'Nachtintensives Gewerbe',
      'L0': 'Landwirtschaft allgemein',
      'L1': 'Landwirtschaft mit Milchwirtschaft',
      'L2': 'Übrige Landwirtschaft'
    };
    return descriptions[profileType];
  }
}

export const settingsService = new SettingsService();
